import React from 'react'
import { useParams, Link } from 'react-router-dom'
import PageHeader from '@/components/Common/PageHeader'
import SEOHead from '@/components/SEO/SEOHead'
import { clampDescription, shortenTitle } from '@/utils/meta'
import { buildGraph, breadcrumbListData, serviceSchema } from '@/data/structured-data'
import { clinicServices } from '@/data/services'

const ClinicDetail: React.FC = () => {
  const { slug } = useParams()
  const service = clinicServices.find(s => s.slug === slug)

  if (!service) {
    return (
      <section className="section-padding">
        <div className="container-custom">
          <div className="bg-white border rounded-xl p-8 text-center">
            <h2 className="text-xl font-bold mb-2">İçerik bulunamadı</h2>
            <p className="text-gray-600 mb-4">Aradığınız klinik bölümü mevcut değil.</p>
            <Link to="/klinik" className="text-primary-600 font-semibold">Kliniğimize dön</Link>
          </div>
        </div>
      </section>
    )
  }

  return (
    <>
      <SEOHead
        title={shortenTitle(service.title)}
        description={clampDescription(service.description, 'Şanlıurfa Karaköprü’deki kliniğimizin bu birimi hakkında bilgi alın.')}
        keywords={`şanlıurfa veteriner, ${service.title.toLowerCase()}, klinik, ameliyathane, röntgen, laboratuvar, acil veteriner`}
        canonicalUrl={`/klinik/${service.slug}`}
        ogImage={service.image}
        structuredData={buildGraph(
          serviceSchema({
            name: service.title,
            description: service.description,
            url: `/klinik/${service.slug}`,
            image: service.image
          }),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: 'Kliniğimiz', url: '/klinik' },
            { name: service.title, url: `/klinik/${service.slug}` }
          ])
        )}
      />
      <PageHeader title={service.title} subtitle={service.description} />
      <section className="section-padding">
        <div className="container-custom grid grid-cols-1 lg:grid-cols-4 gap-8">
          <aside className="lg:col-span-1 space-y-2">
            {clinicServices.map(s => (
              <Link key={s.id} to={`/klinik/${s.slug}`} className={`block px-4 py-2 rounded-lg border ${s.slug===slug? 'bg-primary-50 border-primary-200 text-primary-700':'bg-white hover:bg-gray-50'}`}>
                {s.title}
              </Link>
            ))}
          </aside>
          <article className="lg:col-span-3 bg-white rounded-xl border p-6 shadow">
            {service.image && (
              <img src={service.image} alt={service.title} className="w-full h-64 object-cover rounded-lg mb-6" loading="lazy" onError={(e)=>{(e.currentTarget as HTMLImageElement).src='/klinik.webp'}} />
            )}
            <h2 className="text-xl font-bold mb-3">{service.title}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{service.description}</p>
            {(service.highlights && service.highlights.length>0) && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2">Öne Çıkanlar</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {service.highlights.map((h, idx)=> (
                    <li key={idx} className="flex items-start gap-2 bg-gray-50 border rounded-md p-3">
                      <span className="mt-1 text-primary-600">•</span>
                      <span className="text-gray-700">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(service.preparation && service.preparation.length>0) && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2">Hazırlık</h3>
                <div className="rounded-md border bg-amber-50 p-4">
                  <ul className="list-disc list-inside space-y-1 text-amber-900">
                    {service.preparation.map((p, idx)=> (
                      <li key={idx}>{p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </article>
        </div>
      </section>
    </>
  )
}

export default ClinicDetail


